import { useCallback, useRef, useState } from "react";
import { TranscodeOutcome, transcodeVideo } from "../utils/video-transcoder";

const MAX_UPLOAD_MB = 200; // ceiling on uploaded bytes — mirrors the server's formidable cap
const MAX_SOURCE_MB = 2048; // safety bound on a source we'll attempt to compress in-browser

/**
 * Compress a picked video to a faststart MP4 before the native form submit. Attach the
 * returned `onChange` to a <input type="file">; on pick it transcodes and swaps the
 * element's FileList so the existing form upload carries the smaller file. Also exposes
 * `transcoded` so the form can tell the server whether the bytes still need MediaConvert.
 * The size limit is enforced on the UPLOADED bytes (post-compression). Never throws.
 */
export default function useVideoTranscode({
  onChange,
  maxUploadMb = MAX_UPLOAD_MB,
  maxSourceMb = MAX_SOURCE_MB,
} = {}) {
  const abortRef = useRef(null);
  // The input we last handled, so reset() can clear it. A file input is uncontrolled,
  // so React never clears its value for us.
  const inputRef = useRef(null);
  const [preparing, setPreparing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [tooBig, setTooBig] = useState(false);
  const [transcoded, setTranscoded] = useState(null); // true | false | null
  // Source size of a DEFERRED video, so the server can match its bitrate. null when unreadable.
  const [dimensions, setDimensions] = useState(null);

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  /**
   * Clear the field completely: stop any transcode, empty the FileList that
   * handleChange swapped in, and drop the outcome so the hidden fields stop
   * claiming a video is attached. Re-fires onChange so the parent clears its own
   * videoFile the same way it set it.
   */
  const reset = useCallback(() => {
    abortRef.current?.abort();
    setPreparing(false);
    setProgress(0);
    setTooBig(false);
    setTranscoded(null);
    setDimensions(null);
    const input = inputRef.current;
    if (!input) return;
    input.value = "";
    onChange?.({ target: input });
  }, [onChange]);

  const handleChange = useCallback(
    async (e) => {
      const input = e.target;
      inputRef.current = input;
      const picked = input.files?.[0];
      if (!picked) return;
      console.log(
        "[video-transcode] picked",
        picked.name,
        `${(picked.size / 1e6).toFixed(1)}MB`
      );

      setTranscoded(null);
      setDimensions(null);

      // Reject sources too large to even attempt compressing; the real upload ceiling
      // is enforced on the compressed output below.
      if (picked.size > maxSourceMb * 1024 * 1024) {
        setTooBig(true);
        input.value = "";
        return;
      }
      setTooBig(false);

      const controller = new AbortController();
      abortRef.current = controller;
      setProgress(0);
      setPreparing(true);
      try {
        const outcome = await transcodeVideo(picked, {
          onProgress: setProgress,
          signal: controller.signal,
        });
        console.log(
          `[video-transcode] ${outcome.outcome}${outcome.reason ? ` (${outcome.reason})` : ""}: ${(picked.size / 1e6).toFixed(1)}MB → ${(outcome.file.size / 1e6).toFixed(1)}MB`,
          outcome.detail ?? outcome.meta
        );
        if (controller.signal.aborted) {
          input.value = ""; // cancelled → clear the field so the user can re-pick
          return;
        }
        const isTranscoded = outcome.outcome === TranscodeOutcome.TRANSCODED;
        const file = isTranscoded ? outcome.file : picked;
        // Enforce the limit that matters — on the bytes we're actually about to send.
        if (file.size > maxUploadMb * 1024 * 1024) {
          setTooBig(true);
          input.value = "";
          return;
        }
        // Swap the element's FileList so the native form submit carries the new file.
        const data = new DataTransfer();
        data.items.add(file);
        input.files = data.files;
        // Surface the outcome so the form can route the upload (final vs MediaConvert).
        setTranscoded(isTranscoded);
        const { width, height } = outcome.meta ?? {};
        setDimensions(width > 0 && height > 0 ? { width, height } : null);
        onChange?.({ target: input });
      } finally {
        setPreparing(false);
        abortRef.current = null;
      }
    },
    [onChange, maxUploadMb, maxSourceMb]
  );

  return {
    onChange: handleChange,
    preparing,
    progress,
    tooBig,
    cancel,
    reset,
    transcoded,
    dimensions,
  };
}
